// Enter a supplier's bill line by line against its PO (Stephanie 2026-10-13:
// "more like commonsku's version that also pulls in the pricing that was on
// the PO so we can confirm line by line").
//
// Pick the PO → its items and decoration charges load at the PO's quantity and
// unit cost (our cost). Each line is ticked to bill; type what the supplier
// actually charged. A line that differs from the PO turns amber and asks why
// (the bill page then shows the difference). "Add a charge" covers anything
// the supplier billed that wasn't on the PO (freight, a surcharge, tax).
// The server re-reads the PO figures on save — the browser never sends them.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Plus, Trash2, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { TermsDueHint, useTermsDueDate } from "@/components/finance/TermsDueHint";
import { MentionTextarea } from "@/components/notes/MentionTextarea";
import { SupplierBillFile, type AttachedFile } from "@/components/finance/SupplierBillFile";

export interface BillPoOption {
  id: string;
  poNumber: number;
  vendorName: string;
}

type ChargeType =
  | "product" | "setup_fee" | "custom_pms" | "imprint_cost" | "handling" | "shipping"
  | "rush" | "fuel_surcharge" | "drop_ship" | "special_packaging" | "customer_provided"
  | "tax" | "other";

type VarianceReason =
  | "pricing_drift" | "vendor_surcharge" | "partial_shipment" | "vendor_waived"
  | "qty_change" | "freight_estimate" | "other";

interface TemplateLine {
  key: string;
  kind: "item" | "charge";
  description: string;
  chargeType: ChargeType;
  quantity: string;
  unitCost: string;
}

interface Template {
  po: { id: string; poNumber: number; vendorName: string; vendorTerms?: string | null; cancelled: boolean };
  lines: TemplateLine[];
}

interface Row {
  id: string;
  /** Template key; undefined for a charge that wasn't on the PO. */
  key?: string;
  kind: "item" | "charge" | "extra";
  description: string;
  chargeType: ChargeType;
  poQty: string | null;
  poCost: string | null;
  qty: string;
  cost: string;
  billed: boolean;
  reason: VarianceReason | "";
}

export const REASONS: Array<{ value: VarianceReason; label: string }> = [
  { value: "partial_shipment", label: "Partial shipment" },
  { value: "qty_change", label: "Quantity change" },
  { value: "pricing_drift", label: "Pricing changed" },
  { value: "vendor_surcharge", label: "Vendor surcharge" },
  { value: "freight_estimate", label: "Freight" },
  { value: "vendor_waived", label: "Vendor waived" },
  { value: "other", label: "Other" },
];

const EXTRA_TYPES: Array<{ value: ChargeType; label: string }> = [
  { value: "shipping", label: "Shipping / freight" },
  { value: "setup_fee", label: "Setup" },
  { value: "imprint_cost", label: "Run / imprint" },
  { value: "rush", label: "Rush" },
  { value: "handling", label: "Handling" },
  { value: "tax", label: "Tax" },
  { value: "other", label: "Other" },
];

const FIELD =
  "block h-10 w-full rounded border border-rule bg-white px-3 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]";
const DATE_FIELD = `${FIELD} min-w-0 max-w-full appearance-none text-left`;
const CELL_INPUT =
  "h-9 w-full min-w-[72px] rounded border border-rule bg-white px-2 text-right text-sm tabular-nums text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function num(s: string): number {
  const n = Number(s.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

/** Unit costs can carry more than 2 decimals ($0.125 per piece). */
function unitMoney(s: string | null): string {
  if (s === null) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function qtyText(s: string | null): string {
  if (s === null) return "—";
  const n = Number(s);
  return Number.isFinite(n) ? n.toLocaleString() : s;
}

/** Same test the server and database use. */
export function rowDiffers(r: { poQty: string | null; poCost: string | null; qty: string; cost: string }): boolean {
  return num(r.qty) !== Number(r.poQty ?? 0) || num(r.cost) !== Number(r.poCost ?? 0);
}

/** A sensible first guess at why a line differs; the rep can change it. */
function suggestReason(r: Row): VarianceReason {
  if (r.kind === "extra") return r.chargeType === "shipping" ? "freight_estimate" : "vendor_surcharge";
  if (num(r.qty) !== Number(r.poQty ?? 0)) return num(r.qty) < Number(r.poQty ?? 0) ? "partial_shipment" : "qty_change";
  return "pricing_drift";
}

let extraSeq = 0;

export function BillFromPoModal(props: {
  purchaseOrders: BillPoOption[];
  /** Start on this PO (Billing → Missing a bill → Enter bill). */
  initialPoId?: string | null;
  onCancel: () => void;
  onCreated: (billId: string) => void;
}) {
  const { purchaseOrders, onCancel, onCreated, initialPoId } = props;
  const [poId, setPoId] = useState(
    initialPoId && purchaseOrders.some((p) => p.id === initialPoId) ? initialPoId : purchaseOrders.length === 1 ? purchaseOrders[0]!.id : "",
  );
  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(todayIso());
  const [notes, setNotes] = useState("");
  const [noteMentions, setNoteMentions] = useState<string[]>([]);
  // The supplier's bill (Stephanie 2026-10-22).
  const [billFile, setBillFile] = useState<AttachedFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  const tplQ = useQuery({
    queryKey: ["pos", poId, "bill-template"],
    queryFn: () => api<Template>(`/pos/${poId}/bill-template`),
    enabled: poId !== "",
  });
  // Due date from the vendor's terms until someone types one (Stephanie 2026-10-20).
  const { dueDate, setDueDate, fromTerms } = useTermsDueDate(billDate, tplQ.data?.po.vendorTerms);

  // Fresh lines whenever a PO's pricing loads.
  useEffect(() => {
    if (!tplQ.data) return;
    setRows(
      tplQ.data.lines.map((l) => ({
        id: l.key,
        key: l.key,
        kind: l.kind,
        description: l.description,
        chargeType: l.chargeType,
        poQty: l.quantity,
        poCost: l.unitCost,
        qty: String(Number(l.quantity)),
        cost: String(Number(l.unitCost)),
        billed: true,
        reason: "",
      })),
    );
  }, [tplQ.data]);

  function update(id: string, patch: Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function addCharge() {
    extraSeq += 1;
    setRows((rs) => [
      ...rs,
      {
        id: `extra-${extraSeq}`,
        kind: "extra",
        description: "",
        chargeType: "shipping",
        poQty: null,
        poCost: null,
        qty: "1",
        cost: "",
        billed: true,
        reason: "",
      },
    ]);
  }

  const billed = rows.filter((r) => r.billed);
  const poTotal = useMemo(
    () => rows.reduce((s, r) => s + (r.poQty !== null ? Number(r.poQty) * Number(r.poCost ?? 0) : 0), 0),
    [rows],
  );
  const billTotal = billed.reduce((s, r) => {
    const v = num(r.qty) * num(r.cost);
    return s + (Number.isFinite(v) ? v : 0);
  }, 0);
  const diff = Math.round((billTotal - poTotal) * 100) / 100;
  const differing = billed.filter((r) => rowDiffers(r)).length;

  const create = useMutation({
    mutationFn: () =>
      api<{ bill: { id: string } }>("/bills/from-po", {
        method: "POST",
        body: JSON.stringify({
          purchase_order_id: poId,
          vendor_bill_number: billNumber.trim(),
          bill_date: billDate,
          due_date: dueDate || null,
          notes: notes.trim() || null,
          mention_user_ids: noteMentions,
          file_id: billFile?.fileId ?? null,
          lines: billed.map((r) => ({
            ...(r.key ? { key: r.key } : {}),
            description: r.description.trim(),
            charge_type: r.chargeType,
            quantity: String(num(r.qty)),
            unit_cost: String(num(r.cost)),
            ...(rowDiffers(r) ? { variance_reason: r.reason || suggestReason(r) } : {}),
          })),
        }),
      }),
    onSuccess: (res) => onCreated(res.bill.id),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !create.isPending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [create.isPending, onCancel]);

  function submit() {
    setLocalError(null);
    if (!poId) return setLocalError("Pick the PO this bill is for.");
    // Required (Stephanie 2026-11-03).
    if (!billNumber.trim()) return setLocalError("Enter the supplier's bill number.");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(billDate)) return setLocalError("Bill date is required.");
    if (billed.length === 0) return setLocalError("Tick at least one line to bill.");
    for (const r of billed) {
      if (!r.description.trim()) return setLocalError("Every added charge needs a description.");
      if (!Number.isFinite(num(r.qty)) || num(r.qty) < 0) return setLocalError(`Check the quantity on "${r.description}".`);
      if (!Number.isFinite(num(r.cost)) || num(r.cost) < 0 || r.cost.trim() === "") return setLocalError(`Enter the unit cost on "${r.description || "the added charge"}".`);
    }
    if (!(billTotal > 0)) return setLocalError("The bill total must be more than zero.");
    create.mutate();
  }

  const serverError =
    create.error instanceof ApiError
      ? create.error.status === 409
        ? "This supplier already has a bill with that number, so it wasn't saved. Check the number — if it's the same bill, it's already entered."
        : ((create.error.body as { error?: string } | null)?.error ?? `Couldn't save the bill (${create.error.status}).`)
      : create.error
        ? "Couldn't save the bill. Try again."
        : null;
  const error = localError ?? serverError;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Enter a bill"
    >
      <div className="flex max-h-[94vh] w-full max-w-5xl flex-col rounded-t-md bg-white shadow-xl sm:rounded-md">
        <div className="flex items-start justify-between gap-4 border-b border-rule px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Enter a bill</h2>
            <p className="mt-1 text-xs text-muted">
              The PO&apos;s pricing is filled in. Confirm each line against the supplier&apos;s
              bill and change anything that&apos;s different.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={create.isPending}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted hover:bg-canvas hover:text-ink disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex-1 overflow-y-auto px-6 py-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <label className="block sm:col-span-4">
              <span className="mb-1 block text-xs text-muted">PO (required)</span>
              <select
                autoFocus
                value={poId}
                onChange={(e) => {
                  setPoId(e.target.value);
                  setRows([]);
                }}
                disabled={create.isPending}
                className={FIELD}
              >
                <option value="">Select the PO this bill is for…</option>
                {purchaseOrders.map((p) => (
                  <option key={p.id} value={p.id}>
                    PO #{p.poNumber} · {p.vendorName}
                  </option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-muted">Supplier&apos;s bill # (required)</span>
              <input value={billNumber} onChange={(e) => setBillNumber(e.target.value)} maxLength={120} required aria-required="true" placeholder="From the supplier's bill" className={FIELD} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-muted">Bill date (required)</span>
              <input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} className={DATE_FIELD} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-muted">Due date</span>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={DATE_FIELD} />
              <TermsDueHint vendorName={tplQ.data?.po.vendorName ?? null} terms={tplQ.data?.po.vendorTerms} fromTerms={fromTerms} />
            </label>
          </div>

          {poId !== "" && (
            <div className="mt-5">
              {tplQ.isLoading ? (
                <div className="h-24 animate-pulse rounded-md bg-canvas" aria-label="Loading the PO's pricing" />
              ) : tplQ.isError ? (
                <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg px-3 py-2 text-sm text-risk">
                  Couldn&apos;t load this PO&apos;s pricing. Close and try again.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-md border border-rule" role="region" aria-label="Bill lines" tabIndex={0}>
                    <table className="w-full min-w-[760px] text-left text-[13px]">
                      <thead className="bg-canvas text-[11px] uppercase tracking-[0.06em] text-muted">
                        <tr>
                          <th scope="col" className="w-10 px-3 py-2">Bill</th>
                          <th scope="col" className="px-3 py-2">Item / charge</th>
                          <th scope="col" className="px-3 py-2 text-right">PO qty</th>
                          <th scope="col" className="px-3 py-2 text-right">PO unit cost</th>
                          <th scope="col" className="w-24 px-3 py-2 text-right">Billed qty</th>
                          <th scope="col" className="w-28 px-3 py-2 text-right">Billed unit cost</th>
                          <th scope="col" className="px-3 py-2 text-right">Line total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rule">
                        {rows.length === 0 && (
                          <tr>
                            <td colSpan={7} className="px-3 py-6 text-center text-muted">
                              This PO has no priced lines. Use Add a charge to enter what the supplier billed.
                            </td>
                          </tr>
                        )}
                        {rows.map((r) => {
                          const differs = r.billed && rowDiffers(r);
                          const lineTotal = num(r.qty) * num(r.cost);
                          return (
                            <tr key={r.id} className={cn(!r.billed && "opacity-50", differs && "bg-warn-bg/60")}>
                              <td className="px-3 py-2 align-top">
                                <input
                                  type="checkbox"
                                  aria-label={`Bill ${r.description || "this charge"}`}
                                  className="mt-2 h-4 w-4 accent-ink"
                                  checked={r.billed}
                                  onChange={(e) => update(r.id, { billed: e.target.checked })}
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                {r.kind === "extra" ? (
                                  <div className="flex flex-wrap gap-2">
                                    <select
                                      aria-label="Charge type"
                                      value={r.chargeType}
                                      onChange={(e) => update(r.id, { chargeType: e.target.value as ChargeType })}
                                      className="h-9 rounded border border-rule bg-white px-2 text-sm"
                                    >
                                      {EXTRA_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                      ))}
                                    </select>
                                    <input
                                      aria-label="Description"
                                      value={r.description}
                                      onChange={(e) => update(r.id, { description: e.target.value })}
                                      placeholder="What the supplier charged for"
                                      maxLength={500}
                                      className="h-9 min-w-[180px] flex-1 rounded border border-rule bg-white px-2 text-sm"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded text-muted hover:bg-canvas hover:text-risk"
                                      aria-label="Remove this charge"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <p className={cn("pt-2", r.kind === "item" ? "font-medium text-ink" : "pl-4 text-muted")}>
                                    {r.description}
                                  </p>
                                )}
                                {r.kind === "extra" && (
                                  <p className="mt-1 text-[11px] text-muted">Not on the PO</p>
                                )}
                                {differs && (
                                  <label className="mt-2 flex items-center gap-2 text-xs text-warn">
                                    Why is it different?
                                    <select
                                      aria-label="Why is it different?"
                                      value={r.reason || suggestReason(r)}
                                      onChange={(e) => update(r.id, { reason: e.target.value as VarianceReason })}
                                      className="h-8 rounded border border-warn/40 bg-white px-2 text-xs text-ink"
                                    >
                                      {REASONS.map((x) => (
                                        <option key={x.value} value={x.value}>{x.label}</option>
                                      ))}
                                    </select>
                                  </label>
                                )}
                              </td>
                              <td className="px-3 py-2 pt-4 text-right align-top tabular-nums text-muted">{qtyText(r.poQty)}</td>
                              <td className="px-3 py-2 pt-4 text-right align-top tabular-nums text-muted">{unitMoney(r.poCost)}</td>
                              <td className="px-3 py-2 align-top">
                                <input
                                  aria-label={`Billed quantity for ${r.description || "this charge"}`}
                                  inputMode="decimal"
                                  value={r.qty}
                                  disabled={!r.billed}
                                  onChange={(e) => update(r.id, { qty: e.target.value })}
                                  className={CELL_INPUT}
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <input
                                  aria-label={`Billed unit cost for ${r.description || "this charge"}`}
                                  inputMode="decimal"
                                  value={r.cost}
                                  disabled={!r.billed}
                                  onChange={(e) => update(r.id, { cost: e.target.value })}
                                  placeholder="0.00"
                                  className={CELL_INPUT}
                                />
                              </td>
                              <td className="px-3 py-2 pt-4 text-right align-top font-medium tabular-nums text-ink">
                                <span className="inline-flex items-center gap-1">
                                  {r.billed && !differs && r.kind !== "extra" && (
                                    <Check className="h-3.5 w-3.5 text-ok" aria-label="Matches the PO" />
                                  )}
                                  {r.billed && Number.isFinite(lineTotal) ? money(lineTotal) : "—"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
                    <Button type="button" variant="outline" onClick={addCharge} disabled={create.isPending}>
                      <Plus className="h-4 w-4" /> Add a charge
                    </Button>
                    <dl className="min-w-[260px] space-y-1 text-sm">
                      <div className="flex justify-between gap-6 text-muted">
                        <dt>PO total</dt>
                        <dd className="tabular-nums">{money(poTotal)}</dd>
                      </div>
                      <div className="flex justify-between gap-6 font-semibold text-ink">
                        <dt>Bill total</dt>
                        <dd className="tabular-nums">{money(billTotal)}</dd>
                      </div>
                      <div className={cn("flex justify-between gap-6", diff === 0 ? "text-ok" : "text-warn")}>
                        <dt>{diff === 0 ? "Matches the PO" : diff > 0 ? "More than the PO" : "Less than the PO"}</dt>
                        <dd className="tabular-nums">{diff === 0 ? "" : money(Math.abs(diff))}</dd>
                      </div>
                      {differing > 0 && (
                        <p className="text-xs text-warn">
                          {differing} {differing === 1 ? "line differs" : "lines differ"} from the PO.
                        </p>
                      )}
                    </dl>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="mt-5">
            <span className="mb-1 block text-xs text-muted">Supplier's bill</span>
            <SupplierBillFile value={billFile} onChange={setBillFile} prefix={`pos/${poId}/bills`} onBusy={setUploading} />
          </div>

          <label className="mt-5 block">
            <span className="mb-1 block text-xs text-muted">Notes</span>
            <MentionTextarea
              value={notes}
              onChange={(t, ids) => { setNotes(t); setNoteMentions(ids); }}
              rows={2}
              maxLength={2000}
              placeholder="Optional — type @ to tag someone"
              className="block w-full rounded border border-rule bg-white px-3 py-2 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]"
            />
          </label>

          {error && (
            <div role="alert" className="mt-4 rounded-md border border-risk/30 bg-risk-bg px-3 py-2 text-sm text-risk">
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || uploading || poId === "" || tplQ.isLoading}>
              {create.isPending ? "Saving…" : "Save bill"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
